import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { MissingOrderItems, TimelineItem } from '../types';
import { Eta } from '@tempus/order-status-service-shared';
import { AllReportStatuses } from '@tempus/portal-service-shared';
import formatEta from '../utils/formatEta';
import moment from 'moment';
import MissingItemsAlert from './MissingItemsAlert';
import NodeIcon from './NodeIcon';
import { COMPLETE_STATUSES } from '../constants';
import { BrandPalette, GrayscalePalette, ProgressPalette } from '@tcl-boron-colors/colors';
import logger from '../../../../../utils/logger';
import usePortalContext from '../../../context/usePortalContext';
import Loader from '@tcl-boron-prefabs/loader';
import ErrorScreen from '../../../components/ErrorScreen';

interface ReportStatusProps {
  reportStatus?: AllReportStatuses;
  hasQns: boolean;
}

const ReportStatusView: React.FC<ReportStatusProps> = ({ reportStatus, hasQns }) => {
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [eta, setEta] = useState<Eta>();
  const [missingOrderItems, setMissingOrderItems] = useState<MissingOrderItems>({ patient: [], provider: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const {
    patient: { orderId },
    fetchHelper,
  } = usePortalContext();

  const fetchETA = async () => {
    try {
      const response = await fetchHelper<Eta[]>(`/orders/${orderId}/eta`);

      if (response.length > 0) {
        setEta(response[0]);
      }
    } catch (e) {
      logger.error(e as Error, 'Error fetching ETA');
      throw e;
    }
  };

  const fetchTimeline = async () => {
    try {
      const response = await fetchHelper<TimelineItem[]>(`/orders/${orderId}/timeline`);

      setTimeline(response);
    } catch (e) {
      logger.error(e as Error, 'Error fetching timeline');
      throw e;
    }
  };

  const fetchMissingOrderItems = async () => {
    try {
      const response = await fetchHelper<MissingOrderItems>(`/orders/${orderId}/missing-items`);

      setMissingOrderItems(response);
    } catch (e) {
      logger.error(e as Error, 'Error fetching missing items');
      throw e;
    }
  };

  const fetchData = async () => {
    try {
      await Promise.all([fetchTimeline(), fetchETA(), fetchMissingOrderItems()]);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {loading ? (
        <Grid container flexGrow={1} direction="column" gap="8px" alignItems="center" justifyContent="center">
          <Loader size="large" />
          <Typography variant="body1">Loading</Typography>
        </Grid>
      ) : error ? (
        <Grid container alignItems="center" justifyContent="center" flexGrow={1}>
          <ErrorScreen />
        </Grid>
      ) : (
        <>
          {reportStatus !== AllReportStatuses.COMPLETE && <MissingItemsAlert missingOrderItems={missingOrderItems!} />}
          <Typography variant="h2">PGx Report Status</Typography>
          <Grid container direction="row" gap="8px">
            <Typography fontWeight="700" variant="body1" fontSize="16px" color={BrandPalette[400]}>
              ETA:
            </Typography>
            <Typography fontWeight="400" variant="body1" fontSize="16px" color={BrandPalette[400]}>
              {formatEta(eta, reportStatus, hasQns)}
            </Typography>
          </Grid>
          <Grid container flexGrow={1} direction="column" paddingTop="16px">
            {timeline?.map((item, index) => (
              <Grid
                container
                key={index}
                direction="row"
                wrap="nowrap"
                flexGrow={item.isTerminalNode ? 0 : 1}
                padding="8px 0"
              >
                <Grid
                  item
                  container
                  direction="column"
                  flexBasis="25%"
                  alignItems="start"
                  padding="0px 16px 0 0"
                  gap="8px"
                >
                  <Grid
                    container
                    direction="row"
                    wrap="nowrap"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="16px"
                  >
                    <Grid item textAlign="right" flexGrow={1}>
                      <Typography
                        fontWeight="400"
                        fontSize="10px"
                        color={GrayscalePalette[600]}
                        overflow="hidden"
                        lineHeight="24px"
                        whiteSpace={'nowrap'}
                      >
                        {COMPLETE_STATUSES.includes(item.status) ? moment(item.date).format('MMM D') : null}
                      </Typography>
                    </Grid>
                    <NodeIcon itemStatus={item.status} reportStatus={reportStatus} />
                  </Grid>
                  {!item.isTerminalNode ? (
                    <Grid
                      alignSelf="flex-end"
                      flexGrow={1}
                      marginRight="6px"
                      border={`1px ${
                        COMPLETE_STATUSES.includes(item.status)
                          ? `solid ${ProgressPalette[700]}`
                          : `dashed ${GrayscalePalette[400]}`
                      }`}
                    />
                  ) : null}
                </Grid>
                <Grid
                  container
                  direction="column"
                  gap="4px"
                  justifyContent="start"
                  alignItems="start"
                  alignContent="start"
                >
                  <Typography
                    fontWeight="600"
                    fontSize="14px"
                    lineHeight="24px"
                    color={COMPLETE_STATUSES.includes(item.status) ? GrayscalePalette[700] : GrayscalePalette[500]}
                  >
                    {item.label.toUpperCase()}
                  </Typography>
                  <Typography
                    fontWeight="400"
                    fontSize="12px"
                    lineHeight="14px"
                    color={COMPLETE_STATUSES.includes(item.status) ? GrayscalePalette[700] : GrayscalePalette[500]}
                    whiteSpace="wrap"
                  >
                    {item.subLabel}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </>
  );
};

export default ReportStatusView;
